import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, AlertTriangle } from 'lucide-react'
import { useAuth } from '../auth/AuthProvider'
import { useToast } from '../common/Toast'
import { signOut } from '../../lib/auth'
import { deleteMyData } from '../../lib/account'

interface DeleteAccountModalProps {
  onClose: () => void
}

const DELETED = [
  'Every workshop session you created, including the full chat transcript',
  'Documents you uploaded and the artifacts the agents built from them',
  'Reflections and feedback you submitted during those sessions',
  'Your teams: deleted outright if you are the only member, otherwise you are removed from them',
  'In sessions you joined, your participant record and reflection answers',
  'Your account and sign-in',
]

const RETAINED = [
  'Transcripts of sessions other people created: they belong to that team, but your messages are no longer attributed to you',
]

export default function DeleteAccountModal({ onClose }: DeleteAccountModalProps) {
  const { user } = useAuth()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const canDelete = confirmText.trim() === 'DELETE' && !loading

  function handleClose() {
    if (loading) return
    onClose()
  }

  async function handleDelete() {
    if (!user || !canDelete) return
    setError('')
    setLoading(true)
    try {
      const idToken = await user.getIdToken()
      const summary = await deleteMyData(idToken)
      addToast(
        `Your data is gone. ${summary.sessionsDeleted} session${summary.sessionsDeleted === 1 ? '' : 's'} and ${summary.messagesDeleted} message${summary.messagesDeleted === 1 ? '' : 's'} deleted.`,
        'success',
      )
      await signOut()
      navigate('/')
    } catch {
      setError('Deletion did not complete. Please try again, or email dayone@mvpclub.ai and we will finish it for you.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-navy/50 flex items-center justify-center z-50 p-4 animate-fade-in" onClick={handleClose}>
      <div className="card card-shadow w-full max-w-md p-8 relative animate-modal-enter max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <button onClick={handleClose} className="absolute top-4 right-4 text-navy/70 hover:text-navy transition" aria-label="Close">
          <X size={20} />
        </button>

        <div className="flex items-center gap-2 mb-6">
          <AlertTriangle className="w-5 h-5 text-brick shrink-0" aria-hidden="true" />
          <h2 className="hl text-2xl">Delete Your Data</h2>
        </div>

        {!confirming ? (
          <div className="space-y-4">
            <p className="text-sm text-navy/70 leading-relaxed">
              This permanently removes your data from Day One. It cannot be undone, and we cannot recover it for you afterwards.
            </p>

            <div className="bg-brick/10 border-2 border-brick p-4">
              <p className="mono-label text-brick mb-2">Deleted forever</p>
              <ul className="space-y-1.5">
                {DELETED.map(item => (
                  <li key={item} className="text-sm text-navy/85 leading-snug pl-4 relative">
                    <span className="absolute left-0 top-0">–</span>{item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-stone border border-navy/10 p-4">
              <p className="mono-label text-teal mb-2">Retained</p>
              <ul className="space-y-1.5">
                {RETAINED.map(item => (
                  <li key={item} className="text-sm text-navy/70 leading-snug pl-4 relative">
                    <span className="absolute left-0 top-0">–</span>{item}
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-sm text-navy/70">
              If you want a copy of your sessions, download them from My Sessions before you continue.
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 btn"
              >
                Keep my data
              </button>
              <button
                onClick={() => setConfirming(true)}
                className="flex-1 btn bg-brick text-white border-navy"
              >
                Continue
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-navy/70 leading-relaxed">
              Deleting the account for <span className="font-semibold text-navy">{user?.email || 'this device'}</span>. Type <span className="font-mono font-bold text-navy">DELETE</span> to confirm.
            </p>

            <input
              autoFocus
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={loading}
              className="w-full w-full border-2 border-brick px-3.5 py-2.5 text-sm font-mono tracking-[0.3em] bg-brick/10 outline-none focus:border-navy disabled:opacity-50"
            />

            {error && <p className="text-brick text-sm">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={loading}
                className="flex-1 btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!canDelete}
                className="flex-1 btn bg-brick text-white border-navy"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete everything'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
